import React, { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  RefreshCw, LogOut, MapPin, Eye, Users, CalendarCheck, Mail,
  Globe, ExternalLink, Monitor, Loader2, BarChart3, MousePointerClick,
} from "lucide-react";
import HeatmapPanel from "../components/HeatmapPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "🏳️";
  return cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
};

const fmtDate = (iso) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const RANGES = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
];

const KpiCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  </div>
);

const BarRow = ({ label, count, max, prefix }) => (
  <div className="flex items-center gap-3">
    <div className="w-32 shrink-0 truncate text-sm text-slate-700 flex items-center gap-2">{prefix}{label}</div>
    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-[#D32F2F]/80 rounded-full" style={{ width: `${max ? (count / max) * 100 : 0}%` }} />
    </div>
    <div className="w-10 text-right text-sm font-semibold text-slate-900">{count}</div>
  </div>
);

export default function AdminDashboard({ token, onLogout }) {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState({ consultations: [], newsletters: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [view, setView] = useState("analytics");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async (silent) => {
    if (silent) setRefreshing(true);
    try {
      const [a, l] = await Promise.all([
        fetch(`${API}/api/admin/analytics?days=${range}`, { headers }),
        fetch(`${API}/api/admin/leads`, { headers }),
      ]);
      if (a.status === 401 || l.status === 401) { onLogout(); return; }
      setData(await a.json());
      setLeads(await l.json());
      setUpdatedAt(new Date());
    } catch (e) {
      // ignore transient errors
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line
  }, [range, token]);

  useEffect(() => { fetchAll(false); }, [fetchAll]);
  useEffect(() => {
    const id = setInterval(() => fetchAll(true), 30000);
    return () => clearInterval(id);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-[#D32F2F]" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const series = (data?.timeseries || []).map((p) => ({ date: fmtDate(p.date), views: p.views }));
  const countries = data?.countries || [];
  const cities = data?.cities || [];
  const sources = data?.sources || [];
  const devices = data?.devices || [];
  const maxCountry = Math.max(1, ...countries.map((c) => c.count));
  const maxCity = Math.max(1, ...cities.map((c) => c.count));
  const maxSource = Math.max(1, ...sources.map((s) => s.count));
  const cons = leads.consultations || [];

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-2.5 h-7 bg-[#D32F2F] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm sm:text-base font-extrabold tracking-tight text-slate-900 leading-none truncate">
                <span className="hidden sm:inline">Redline </span>Analytics
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 hidden sm:block">Admin Console</div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => fetchAll(true)}
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-2.5 sm:px-3.5 py-2 text-sm font-medium transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 bg-[#D32F2F] hover:bg-[#b71c1c] text-white rounded-lg px-2.5 sm:px-3.5 py-2 text-sm font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* View tabs + range + updated */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
              <button
                data-testid="admin-tab-analytics"
                onClick={() => setView("analytics")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === "analytics" ? "bg-[#D32F2F] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <BarChart3 className="w-4 h-4" /> Analytics
              </button>
              <button
                data-testid="admin-tab-heatmap"
                onClick={() => setView("heatmap")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === "heatmap" ? "bg-[#D32F2F] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <MousePointerClick className="w-4 h-4" /> Heatmap
              </button>
            </div>
            <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  data-testid={`admin-range-${r.days}`}
                  onClick={() => setRange(r.days)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    range === r.days ? "bg-[#D32F2F] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {updatedAt && (
            <span className="text-xs text-slate-400">
              Updated {updatedAt.toLocaleTimeString()} · auto-refreshes every 30s
            </span>
          )}
        </div>

        {view === "heatmap" ? (
          <HeatmapPanel token={token} days={range} pages={data?.top_pages} />
        ) : (
        <>
        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard testid="kpi-pageviews" icon={Eye} label="Pageviews" value={k.pageviews ?? 0} accent="bg-[#D32F2F]/10 text-[#D32F2F]" />
          <KpiCard testid="kpi-visitors" icon={Users} label="Unique Visitors" value={k.unique_visitors ?? 0} accent="bg-blue-500/10 text-blue-600" />
          <KpiCard testid="kpi-consultations" icon={CalendarCheck} label="Consultations" value={k.consultations ?? 0} accent="bg-emerald-500/10 text-emerald-600" />
          <KpiCard testid="kpi-newsletters" icon={Mail} label="Newsletter Signups" value={k.newsletters ?? 0} accent="bg-amber-500/10 text-amber-600" />
        </div>

        {/* Chart */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Pageviews over time</h2>
          <div className="h-64" data-testid="admin-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="rl" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#D32F2F" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#D32F2F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} minTickGap={20} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Area type="monotone" dataKey="views" stroke="#D32F2F" strokeWidth={2} fill="url(#rl)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cities + Countries + Sources + Devices */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-cities">
            <div className="flex items-center gap-2 mb-4 text-slate-700"><MapPin className="w-4 h-4" /><h2 className="text-sm font-semibold">Top Cities</h2></div>
            <div className="space-y-3">
              {cities.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
              {cities.map((c, i) => (
                <BarRow key={i} prefix={<span className="text-base leading-none">{flagEmoji(c.code)}</span>} label={c.city} count={c.count} max={maxCity} />
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-countries">
            <div className="flex items-center gap-2 mb-4 text-slate-700"><Globe className="w-4 h-4" /><h2 className="text-sm font-semibold">Top Countries</h2></div>
            <div className="space-y-3">
              {countries.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
              {countries.map((c, i) => (
                <BarRow key={i} prefix={<span className="text-base leading-none">{flagEmoji(c.code)}</span>} label={c.country} count={c.count} max={maxCountry} />
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-sources">
            <div className="flex items-center gap-2 mb-4 text-slate-700"><ExternalLink className="w-4 h-4" /><h2 className="text-sm font-semibold">Top Referring Sources</h2></div>
            <div className="space-y-3">
              {sources.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
              {sources.map((s, i) => (
                <BarRow key={i} label={s.source} count={s.count} max={maxSource} />
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-devices">
            <div className="flex items-center gap-2 mb-4 text-slate-700"><Monitor className="w-4 h-4" /><h2 className="text-sm font-semibold">Devices</h2></div>
            <div className="space-y-3">
              {devices.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
              {devices.map((d, i) => {
                const total = devices.reduce((a, b) => a + b.count, 0) || 1;
                return (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{d.device}</span>
                    <span className="text-slate-500">{d.count} · {Math.round((d.count / total) * 100)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recent leads */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="admin-leads">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-700">Recent Consultation Requests</h2>
            <span className="text-xs text-slate-400">{cons.length} total</span>
          </div>

          {cons.length === 0 && <p className="text-sm text-slate-400">No consultation leads yet.</p>}

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            {cons.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 uppercase text-xs">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">Community</th>
                    <th className="py-2 pr-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {cons.slice(0, 25).map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-2.5 pr-4 font-medium text-slate-900">{c.name}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{c.email}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{c.phone}</td>
                      <td className="py-2.5 pr-4 text-slate-600">{c.community || "—"}</td>
                      <td className="py-2.5 pr-4 text-slate-500">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {cons.slice(0, 25).map((c, i) => (
              <div key={i} className="border border-slate-100 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <span className="font-semibold text-slate-900">{c.name}</span>
                  <span className="text-xs text-slate-400">{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : ""}</span>
                </div>
                <div className="text-sm text-slate-600 mt-1">{c.email}</div>
                <div className="text-sm text-slate-600">{c.phone}</div>
                {c.community && <div className="text-xs text-slate-400 mt-1">{c.community}</div>}
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
}
